import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Heart, Bookmark, MessageSquare, Star, Lock } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import BookmarkButton from './BookmarkButton';

// Placeholder button with tooltip
const PlaceholderButton = ({ icon: Icon, label, count }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button 
          className="flex items-center gap-2 px-3 py-2 bg-[#121917] border border-[#52ea88]/10 text-[#9FBFB4] hover:border-[#52ea88]/30 hover:text-[#E6FFF6] transition-all cursor-not-allowed opacity-70"
          disabled
        >
          <Icon className="w-4 h-4" />
          {count !== undefined && <span className="text-sm">{count}</span>}
        </button>
      </TooltipTrigger>
      <TooltipContent 
        side="top" 
        className="bg-[#0B0F0E] border border-[#52ea88]/30 text-[#E6FFF6] text-xs p-2"
      >
        <div className="flex items-center gap-1">
          <Lock className="w-3 h-3 text-[#52ea88]" />
          <span>{label} — Coming Soon</span>
        </div>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// User Actions Bar (for review pages)
export const UserActionsBar = ({ reviewId }) => (
  <div className="flex items-center gap-3">
    <PlaceholderButton icon={Heart} label="Add to Favorites" count={0} />
    <BookmarkButton reviewId={reviewId} />
    <PlaceholderButton icon={Star} label="Rate this Film" />
  </div>
);

// User Rating Input (placeholder)
export const UserRatingPlaceholder = () => (
  <div className="p-4 bg-[#121917] border border-[#52ea88]/10">
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-sm font-medium text-[#E6FFF6]">Your Rating</h4>
      <span className="text-xs text-[#52ea88]/70 uppercase tracking-wider flex items-center gap-1">
        <Lock className="w-3 h-3" />
        Coming Soon
      </span>
    </div>
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          disabled
          className="p-1 text-[#9FBFB4]/30 cursor-not-allowed"
        >
          <Star className="w-6 h-6" />
        </button>
      ))}
    </div>
    <p className="text-xs text-[#9FBFB4]/60 mt-2">
      User ratings will be separate from Matrix Signals
    </p>
  </div>
);

// Comments Section (placeholder)
export const CommentsPlaceholder = () => (
  <div className="p-6 bg-[#121917] border border-[#52ea88]/10">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-[#E6FFF6] flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-[#52ea88]" />
        Discussion
      </h3>
      <span className="text-xs text-[#52ea88]/70 uppercase tracking-wider flex items-center gap-1">
        <Lock className="w-3 h-3" />
        Coming Soon
      </span>
    </div>
    
    <div className="space-y-4">
      {/* Placeholder comment input */}
      <div className="relative">
        <textarea
          disabled
          placeholder="Share your thoughts on this film..."
          className="w-full p-4 bg-[#0B0F0E] border border-[#52ea88]/10 text-[#9FBFB4]/50 placeholder-[#9FBFB4]/30 resize-none h-24 cursor-not-allowed"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-[#0B0F0E]/50">
          <div className="text-center">
            <Lock className="w-6 h-6 text-[#52ea88]/50 mx-auto mb-2" />
            <p className="text-sm text-[#9FBFB4]/70">Community features coming soon</p>
          </div>
        </div>
      </div>
      
      {/* Sample placeholder comments */}
      <div className="space-y-3 opacity-40">
        <div className="flex gap-3 p-3 bg-[#0B0F0E]/50 border border-[#52ea88]/5">
          <div className="w-8 h-8 bg-[#52ea88]/10 rounded-full flex-shrink-0"></div>
          <div className="flex-1">
            <div className="h-3 w-24 bg-[#52ea88]/10 mb-2"></div>
            <div className="h-2 w-full bg-[#52ea88]/5 mb-1"></div>
            <div className="h-2 w-3/4 bg-[#52ea88]/5"></div>
          </div>
        </div>
        <div className="flex gap-3 p-3 bg-[#0B0F0E]/50 border border-[#52ea88]/5">
          <div className="w-8 h-8 bg-[#52ea88]/10 rounded-full flex-shrink-0"></div>
          <div className="flex-1">
            <div className="h-3 w-32 bg-[#52ea88]/10 mb-2"></div>
            <div className="h-2 w-full bg-[#52ea88]/5 mb-1"></div>
            <div className="h-2 w-1/2 bg-[#52ea88]/5"></div>
          </div>
        </div>
      </div>
    </div>
    
    <p className="text-xs text-[#9FBFB4]/50 mt-4 text-center">
      User interaction will be enabled in a future update
    </p>
  </div>
);

// Movie Card Actions (for grid cards)
export const CardActionsPlaceholder = () => (
  <div className="absolute top-4 right-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button 
            className="p-2 bg-[#0B0F0E]/80 backdrop-blur-sm text-[#9FBFB4] hover:text-[#52ea88] transition-colors cursor-not-allowed"
            disabled
          >
            <Heart className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="bg-[#0B0F0E] border border-[#52ea88]/30 text-xs">
          Favorites — Coming Soon
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button 
            className="p-2 bg-[#0B0F0E]/80 backdrop-blur-sm text-[#9FBFB4] hover:text-[#52ea88] transition-colors cursor-not-allowed"
            disabled
          >
            <Bookmark className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="bg-[#0B0F0E] border border-[#52ea88]/30 text-xs">
          Watchlist — Coming Soon
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </div>
);

export default {
  UserActionsBar,
  UserRatingPlaceholder,
  CommentsPlaceholder,
  CardActionsPlaceholder,
};
